<script>
export default {
  components: {},
  // Disable automatic attribute inheritance, so that $attrs are
  // passed to the <input>, even if it's not the root element.
  // https://vuejs.org/v2/guide/components-props.html#Disabling-Attribute-Inheritance
  inheritAttrs: false,
  // Change the v-model event name to `update` to avoid changing
  // the behavior of the native `input` event.
  // https://vuejs.org/v2/guide/components-custom-events.html#Customizing-Component-v-model
  model: {
    event: 'update',
  },
  props: {
    type: {
      type: String,
      default: 'text',
      // Only allow types that essentially just render text boxes.
      validator(value) {
        return ['email', 'number', 'password', 'search', 'tel', 'text', 'textarea', 'url'].includes(value);
      },
    },
    error: {
      type: [String, Array],
      required: false,
      default: null,
    },
    light: {
      type: Boolean,
      required: false,
    },
    small: {
      type: Boolean,
      required: false,
    },
    label: {
      type: String,
      required: false,
      default: null,
    },
    classStyle: {
      type: String,
      default: '',
    },
  },
  computed: {
    multiError() {
      return this.error && Array.isArray(this.error);
    },
  },
};
</script>

<template>
  <div
    :class="[
      'input-wrapper',
      {
        'input-with-label': (type && type.toLowerCase() === 'text') || type.toLowerCase() === 'password',
      },
    ]"
  >
    <input
      :type="type"
      :class="['input', classStyle, { light: light, small: small }]"
      v-bind="
        $attrs
        // https://vuejs.org/v2/guide/components-props.html#Disabling-Attribute-Inheritance
      "
      @input="$emit('update', $event.target.value)"
      v-on="
        $listeners
        // https://vuejs.org/v2/guide/components-custom-events.html#Binding-Native-Events-to-Components
      "
    />
    <span v-if="label" class="input-side-label">
      {{ label }}
    </span>

    <span v-if="error && !multiError" class="input-error-text">{{ error }}</span>
    <span v-if="error && multiError" class="input-error-text">
      <span class="input-error-multi-msg" v-for="(msg, i) in error" :key="i">{{ msg }}</span>
    </span>
  </div>
</template>

<style lang="scss">
@import '@design';

.input-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.input,
.input-textarea {
  @extend %typography-small;
  outline: none;
  background-color: $color-input-bg;
  border: 1px solid $color-input-bg;
  border-radius: $base-border-radius;
  display: block;
  line-height: 1;
  margin-bottom: 1.5rem;
  padding: $size-input-padding-vertical $size-input-padding-horizontal;
  transition: background $duration-animation-base, border $duration-animation-base;
  width: 100%;

  &:focus {
    border: 1px solid $color-input-focus-border;
  }

  &.light {
    background-color: white;
  }

  &.small {
    font-size: 16px;
    height: 2em;
    margin: 0;
    margin-right: 0.5em;
    padding: 0;
    width: 2em;
  }
  &.user {
    font-weight: 400;
    width: 80%;
    height: 8.93%;
    font-family: $secondary-font-family;
    font-size: 16px;
    line-height: 1;
    padding: 1.31% 5% 3.12% 11%;
    background: transparent;
    border: none;
    border-block-end: 1px solid rgba(0, 0, 0, 0.3);
    outline: transparent;
    background: url('~@assets/images/icons/user-icon-input.svg') 5px no-repeat;
    background-position: 5% 37%;
  }

  &.password {
    width: 80%;
    font-weight: 400;
    height: 8.93%;
    font-family: $secondary-font-family;
    font-size: 16px;
    padding: 1.31% 5% 3.12% 11%;
    background: transparent;
    border: none;
    border-block-end: 1px solid rgba(0, 0, 0, 0.3);
    outline: transparent;
    background: url('~@assets/images/icons/password-icon-input.svg') 5px no-repeat;
    background-position: 5% 22%;
  }
  &.default {
    height: 100%;
    background-color: rgba(237, 238, 243, 0.5);
    border: 1px solid $color-input-bg;
    border-radius: $base-border-radius;
    display: block;
    line-height: 1.6;
    margin-bottom: 1.2rem;
    padding: $size-input-padding-vertical $size-input-padding-horizontal;
    transition: background $duration-animation-base, border $duration-animation-base;
    width: 100%;
    outline: none;
  }
}

.input-with-label {
  width: 100%;
}
.input-side-label {
  background-color: $color-light-gray;
  border: 1px solid $color-input-bg;
  border-radius: $base-border-radius;
  color: $color-input-text;
  font-size: 12px;
  font-weight: bold;
  height: 4.5rem;
  padding: 1.5rem 0.75rem;
  position: absolute;
  right: -2.5rem;
  top: 3.2rem;
  width: 4rem;
}

.input-error {
  .input,
  .input-textarea {
    background-color: $color-input-error-light;
    border: 1px solid $color-input-error-dark;
    margin-bottom: 1.2rem;
  }

  .input-error-text {
    color: $color-input-error-dark;
    display: block;
    margin-bottom: 3.2rem;
    text-align: start;
    // position: absolute;
  }

  .input-error-multi-msg {
    display: block;
  }
}
</style>
